import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { getEntityAcess } from "../settings/users/invite-modal/InviteModalSlice";
import { fetchServices } from "../global-entities/globalEntitiesSlice";
import apiService from "~/services/service";
import { API_ROUTES } from "~/api-routes";
import { fetchEntities } from "~/Pages/Requests/components/new-request-modal/utils";
import { CATEGORIES } from "~/utils/categoryUtils";
import Categories from "../../../src/assets/categories-subcategories.json";
import {
  setFilterServices,
  setPayrollPlusTemplate,
} from "~/Pages/Process/actions/attachmentsSlice";
import { REQUEST_REASON_CODES } from "~/utils/useFileUpload";

const useBlobFilter = ({
  showCategory,
  showServiceLine,
  showYears,
  showEntity,
  onApply,
  serviceLine,
  onFilterChange,
  template,
  showRequestType,
  entityId,
}) => {
  const treeSelectRef = useRef();
  const { isAdmin } = useSelector((state) => state.login);

  const [distance, setDistance] = useState(0);
  const [processType, setProcessType] = useState([]);
  const [filterCount, setFilterCount] = useState(0);
  const [allEntities, setAllEntities] = useState([]);
  const [selectedNodes, setSelectedNodes] = useState({});
  const [value, setValue] = useState({});
  const [group, setGroup] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const divRef = useRef(null);
  const dispatch = useDispatch();
  const { id: userId } = useSelector((state) => state.login);
  const entityservices = useSelector(
    (state) => state.globalEntities.entityservices,
  );
  const { useraccess } = useSelector((state) => state.inviteusers);

  const fetchProcessTypes = useCallback(async () => {
    try {
      const { data } = await apiService(API_ROUTES.FETCH_PROCESS_TYPE);
      setProcessType(data);
    } catch (error) {
      console.error("Error fetching process types:", error);
    }
  }, []);
  const uniqueServiceIds = useMemo(
    () =>
      useraccess?.find((u) => u.id === entityId)?.services?.map((s) => s.id) ??
      [],
    [useraccess, entityId],
  );

  let accessibleServices = useMemo(() => {
    if (!entityservices?.length) return [];
    return entityservices
      .filter((service) => {
        return isAdmin ? true : uniqueServiceIds.includes(service?.id);
      })
      .map((service) => ({
        id: service?.id,
        name: service?.name,
        suboptions: processType
          ?.filter((proc) => proc?.service === service?.id)
          ?.map((proc) => ({
            id: proc?.id,
            name: proc?.name,
          })),
      }));
  }, [entityservices, uniqueServiceIds, processType]);
  useEffect(() => {
    dispatch(setFilterServices(accessibleServices));
  }, [accessibleServices]);

  const buildTreeNodes = useCallback(
    (nodes = [], parentKey = null) => {
      return nodes.map((node) => {
        const nodeKey = node.code || node.id;
        const baseName = node.name || node.label || node.code;

        const children = node.suboptions?.length
          ? buildTreeNodes(node.suboptions, nodeKey)
          : node.subCategory?.length
            ? buildTreeNodes(node.subCategory, nodeKey)
            : [];
        let selectedCount = 0;
        const countSelectedLeaves = (childNodes) => {
          childNodes.forEach((child) => {
            if (!child.children || child.children.length === 0) {
              if (value[child.key]?.checked) {
                selectedCount++;
              }
            } else {
              countSelectedLeaves(child.children);
            }
          });
        };
        countSelectedLeaves(children);

        const label =
          selectedCount > 0 && children.length > 0
            ? `${baseName} (${selectedCount})`
            : baseName;

        return {
          key: nodeKey,
          label,
          rawName: baseName,
          parentKey,
          groupKey: parentKey,
          selectable: true,
          children,
        };
      });
    },
    [value],
  );
  const REQUEST_TYPE_FILTER_OPTION = {
    id: "request_type",
    name: "Request Type",
    suboptions: Object.entries(REQUEST_REASON_CODES).map(([label, code]) => ({
      id: code,
      code,
      name: label,
    })),
  };
  const CATEGORY_FILTER_OPTION = (() => {
    const categoryData = serviceLine ? Categories[serviceLine] : CATEGORIES;
    const formattedCategories = categoryData.map((cat) => ({
      id: cat.code,
      code: cat.code,
      name: cat.name === "Other" ? "Other Document" : cat.name,
      selectable: true,
      suboptions:
        cat.subCategory?.map((sub) => ({
          id: `${cat.code}/${sub.code}`,
          code: `${cat.code}/${sub.code}`,
          name: sub.name,
          suboptions: sub.subCategory || [],
        })) || [],
    }));
    return {
      id: "category",
      name: "Category",
      suboptions: formattedCategories,
    };
  })();

  const fetchEntitiesData = async () => {
    try {
      const { data } = await fetchEntities();
      setAllEntities(data);
    } catch (error) {
      console.error(error);
    }
  };
  const payrollPlusTemplate = useMemo(() => {
    return accessibleServices
      ?.find((data) => {
        return data?.name === "Payroll +";
      })
      ?.suboptions?.map((data) => {
        return data?.id;
      });
  }, [accessibleServices]);
  useEffect(() => {
    if (payrollPlusTemplate?.length) {
      dispatch(setPayrollPlusTemplate(payrollPlusTemplate));
    }
  }, [payrollPlusTemplate]);
  const entitiesSubOptions = useMemo(() => {
    return allEntities?.map(({ id, name }) => ({ name, code: id })) || [];
  }, [allEntities]);
  const groupedOptions = useMemo(() => {
    const options = [];
    let serviceData;
    if (showCategory) {
      options.push(CATEGORY_FILTER_OPTION);
    }

    if (showServiceLine) {
      const hasPayrollPlusAccess = accessibleServices?.some(
        (service) => service?.name?.trim().toLowerCase() === "payroll +",
      );

      serviceData = accessibleServices;

      if (hasPayrollPlusAccess) {
        serviceData = accessibleServices.map((service) => {
          return {
            ...service,
            suboptions:
              service?.suboptions?.map((sub) => ({
                ...sub,
                suboptions: sub?.suboptions || [],
              })) || [],
          };
        });
      }

      if (template) {
        serviceData = serviceData?.filter(
          (service) =>
            service.name === template ||
            service.suboptions?.some((sub) => sub.name === template),
        );
      }
      options.push({
        id: "service_line",
        name: "Service Line & Processes",
        suboptions: serviceData,
      });
    }
    if (showEntity) {
      options.push({
        name: "Entities",
        id: "entities",
        suboptions: entitiesSubOptions || [],
      });
    }
    if (showYears) {
      const currentYear = new Date().getFullYear();
      const yearRange = Array.from(
        { length: 12 },
        (_, i) => currentYear - 10 + i,
      );
      const yearOptions = yearRange.map((year) => ({
        id: `${year}`,
        code: `${year}`,
        name: `${year}`,
      }));

      options.push({
        id: "year",
        name: "Year",
        suboptions: yearOptions,
      });
    }
    if (showRequestType) {
      options.push(REQUEST_TYPE_FILTER_OPTION);
    }

    return options;
  }, [
    CATEGORY_FILTER_OPTION,
    accessibleServices,
    entitiesSubOptions,
    showRequestType,
  ]);

  const treeData = useMemo(() => {
    if (!groupedOptions?.length) return [];

    return groupedOptions.map((group) => {
      const groupKey = group.id || group.code || group.name;
      const children =
        group.suboptions?.length || group.subCategory?.length
          ? buildTreeNodes(group.suboptions || group.subCategory, groupKey)
          : (group.items || []).map((item) => ({
              key: item.id || item.code || item.name,
              label: item.name || item.label || item.code,
              rawName: item.name || item.label || item.code,
              parentKey: groupKey,
              selectable: true,
              groupKey: groupKey,
              children: [],
            }));

      // const hasSelectedDescendant = (node) => {
      //   if (!node.children || node.children.length === 0) {
      //     return !!value[node.key]?.checked;
      //   }
      //   return node.children.some(hasSelectedDescendant);
      // };
      // const selectedParentCount = children.filter(hasSelectedDescendant).length;

      const baseLabel = group.name || group.label || "Group";
      // const label =
      //   selectedParentCount > 0
      //     ? `${baseLabel} (${selectedParentCount})`
      //     : baseLabel;
      return {
        key: groupKey,
        label: baseLabel,
        rawName: baseLabel,
        selectable: false,
        children,
      };
    });
  }, [
    JSON.stringify(groupedOptions),
    JSON.stringify(buildTreeNodes),
    // JSON.stringify(value),
  ]);
  const extractGroupedValues = useCallback((treeValue, nodes) => {
    const grouped = {};

    nodes.forEach((group) => {
      const groupKey = group.key;

      const isFlatGroup = ["year", "entities", "request_type"].includes(
        groupKey,
      );

      if (isFlatGroup) {
        const selected = group.children
          .filter((child) => treeValue[child.key]?.checked)
          .map((child) => child.key);

        grouped[groupKey] = selected;
        return;
      }
      const subgroup = {};
      group.children.forEach((child) => {
        const isChildChecked =
          treeValue[child.key]?.checked || treeValue[child.key]?.partialChecked;
        if (child.children?.length) {
          const nested = extractGroupedValues(treeValue, [child]);
          if (Object.keys(nested).length) {
            subgroup[child.key] = nested[child.key] || [];
          } else if (isChildChecked) {
            subgroup[child.key] = [];
          }
        } else if (isChildChecked) {
          if (!subgroup[groupKey]) subgroup[groupKey] = [];
          subgroup[groupKey].push(child.key);
        }
      });

      if (Object.keys(subgroup).length) {
        grouped[groupKey] = subgroup[groupKey] || subgroup;
      } else if (
        treeValue[groupKey]?.checked ||
        treeValue[groupKey]?.partialChecked
      ) {
        grouped[groupKey] = [];
      }
    });

    return grouped;
  }, []);

  const onChange = useCallback(
    (e) => {
      const newValue = { ...e.value };

      const attachMeta = (nodes = [], parentKey = null) => {
        nodes.forEach((node) => {
          if (newValue[node.key]) {
            newValue[node.key] = {
              ...newValue[node.key],
              id: node.key,
              name: node.rawName || node.label,
              parent: parentKey, // 🔥 FIX: ALWAYS attach parent
            };
          }
          if (node.children?.length) {
            attachMeta(node.children, node.key);
          }
        });
      };

      attachMeta(treeData); // 🔥 Now works for all groups

      setValue(newValue);

      const grouped = extractGroupedValues(newValue, treeData);
      setGroup(grouped);

      if (typeof onFilterChange === "function") {
        onFilterChange({ value: newValue, grouped });
      }
    },
    [treeData, extractGroupedValues, onFilterChange],
  );

  const leafNodeKeys = useMemo(() => {
    const keys = [];
    const collectLeafNodes = (nodes = []) => {
      nodes.forEach((node) => {
        if (!node.children || node.children.length === 0) {
          keys.push(node.key);
        } else {
          collectLeafNodes(node.children);
        }
      });
    };

    collectLeafNodes(treeData);
    return keys;
  }, [treeData]);

  const applyFilter = () => {
    onApply({ value, group });
  };
  const deSelectAll = () => {
    setValue({});
    setGroup([]);
    setSelectedNodes({});
    onApply({ value: {}, group: [] });
  };
  useEffect(() => {
    const countSelected = Object.values(value || {}).filter(
      (value) =>
        ![
          "category",
          "service_line",
          "entities",
          "year",
          "request_type",
        ].includes(value?.id),
    ).length;
    setFilterCount(countSelected);
  }, [value]);

  useEffect(() => {
    dispatch(fetchServices());
    dispatch(getEntityAcess(userId));
    fetchProcessTypes();
    fetchEntitiesData();
  }, [dispatch, userId, fetchProcessTypes]);

  useEffect(() => {
    const element = divRef.current;
    if (element) {
      const rect = element.getBoundingClientRect();
      const distanceFromRight = window.innerWidth - rect.right;
      setDistance(distanceFromRight);
    }
  }, []);

  const removeSelectedItem = useCallback(
    (idToRemove) => {
      if (idToRemove === "ALL_CLEAR") {
        setValue({});
        setGroup({ year: [], service_line: {}, entities: [] });
        onFilterChange({ year: [], service_line: {}, entities: [] });
        return { value: {}, grouped: {} };
      }

      let updated = { ...value };

      const parentKey = updated[idToRemove]?.parent;
      const isFlatLeaf = parentKey === "year" || parentKey === "entities";
      if (isFlatLeaf) {
        delete updated[idToRemove];
        const remaining = Object.keys(updated).filter(
          (k) => updated[k]?.parent === parentKey,
        );
        const grouped = {
          ...group,
          [parentKey]: remaining,
        };
        updated[parentKey] = {
          id: parentKey,
          name: parentKey === "year" ? "Year" : parentKey,
          checked: false,
          partialChecked: false,
          parent: null,
        };
        setValue(updated);
        setGroup(grouped);
        setSelectedNodes(updated);

        if (onFilterChange) onFilterChange(grouped);
        return { value: updated, grouped };
      }
      const findNode = (nodes, key) => {
        for (const node of nodes) {
          if (node.key === key) return node;
          if (node.children?.length) {
            const found = findNode(node.children, key);
            if (found) return found;
          }
        }
        return null;
      };
      const getLeafKeys = (node) => {
        if (!node.children?.length) return [node.key];
        return node.children.flatMap((c) => getLeafKeys(c));
      };

      const updateParentState = (parentKey) => {
        const parentNode = findNode(treeData, parentKey);
        if (!parentNode) return;
        const leafKeys = getLeafKeys(parentNode);
        const selectedChildren = leafKeys.filter((k) => updated[k]?.checked);
        if (selectedChildren.length === 0) {
          delete updated[parentKey];
        } else if (selectedChildren.length === leafKeys.length) {
          updated[parentKey] = {
            id: parentNode.key,
            name: parentNode.rawName,
            checked: true,
            partialChecked: false,
            parent: parentNode.parentKey,
          };
        } else {
          updated[parentKey] = {
            id: parentNode.key,
            name: parentNode.rawName,
            checked: false,
            partialChecked: true,
            parent: parentNode.parentKey,
          };
        }
      };
      const nodeToDelete = findNode(treeData, idToRemove);

      if (nodeToDelete?.children?.length) {
        getLeafKeys(nodeToDelete).forEach((k) => delete updated[k]);
      }
      delete updated[idToRemove];
      let current = value[idToRemove]?.parent;
      while (current) {
        updateParentState(current);
        current = value[current]?.parent;
      }
      setValue(updated);
      const grouped = extractGroupedValues(updated, treeData);
      setGroup(grouped);
      setSelectedNodes(updated);

      if (onFilterChange) onFilterChange(grouped);
      return { value: updated, grouped };
    },
    [value, treeData, extractGroupedValues, group, onFilterChange],
  );

  return {
    treeSelectRef,
    distance,
    setDistance,
    treeData,
    value,
    setValue,
    onChange,
    applyFilter,
    deSelectAll,
    groupedOptions,
    divRef,
    filterCount,
    extractGroupedValues,
    removeSelectedItem,
    filterValue,
    setFilterValue,
    selectedNodes,
    setSelectedNodes,
  };
};

export default useBlobFilter;
